import { FileTypes, SpiderService } from '../services/scraper';
import type { WithPaginationResult } from '../hooks';
import type {
  CompanyInfo,
  PublicParsePayload,
  TasksDetails,
  TaskStatus,
  CampaignsListResponse,
  ScreenshotTypes,
  TaskStatusMetrics,
  Body_upload_screenshot_view_scraper_api_v1_spider_tasks__task_id__screenshot__patch,
} from '../services/scraper';
import type {
  PaginationInfo,
  TaskDetails,
  TaskResponse,
  TasksResponse,
  TaskStatusResponse,
} from '../services/scraper';
import { getCurrentOrg } from './getCurrentOrg';

type TaskData = TaskDetails;
type TaskDetailsData = TaskData & {
  screenshot?: string;
  id?: string;
};

export type TasksDetailsResponse = {
  tasks: Array<TaskDetailsData>;
  pagination: PaginationInfo;
};
export type TaskStatusKey = keyof typeof TaskStatus;
export type TaskStatusValue = (typeof TaskStatus)[TaskStatusKey];
export type TaskPayload = PublicParsePayload;
export type TaskResult = TaskResponse | TasksResponse | TaskStatusResponse;
export type CompanyData = CompanyInfo;
export type TasksDetailsResult = TasksDetails;

export const getScrapingList = async (
  page: number,
  limit: number,
  url?: string | null,
  campaing_name?: string | null,
  status?: TaskStatusValue | null
): Promise<WithPaginationResult<TasksDetailsResponse>> => {
  const response = await SpiderService.getTasksViewScraperApiV1SpiderTasksGet(
    page,
    limit,
    url,
    campaing_name,
    status,
    getCurrentOrg()
  );

  return {
    content: [
      {
        tasks: response.tasks || [],
        pagination: response.pagination,
      },
    ],
  };
};

export const getStatusMetrics = async (
  url?: string | null,
  campaignName?: string | null
): Promise<TaskStatusMetrics> => {
  return SpiderService.statusMetricsViewScraperApiV1SpiderMetricsStatusBreakdownGet(
    url || null,
    campaignName || null,
    getCurrentOrg()
  );
};

export const addScrapingTask = (payload: TaskPayload): Promise<TaskResult> => {
  return SpiderService.publicParseViewScraperApiV1SpiderPublicParsePost({
    ...payload,
    organization_id: getCurrentOrg(),
  });
};

export const retryScrapingTask = (taskId: string): Promise<TaskResult> => {
  return SpiderService.retryTaskViewScraperApiV1SpiderTasksTaskIdRetryPost(
    taskId,
    getCurrentOrg()
  );
};

export const cancelScrapingTask = (taskId: string): Promise<void> => {
  return SpiderService.cancelTaskViewScraperApiV1SpiderTasksTaskIdCancelDelete(
    taskId,
    getCurrentOrg()
  );
};

export const bulkScrapingTasksExport = (
  taskIds: string[] | null,
  url?: string | null,
  campaignName?: string | null
): Promise<TasksDetailsResult> => {
  const taskIdsToPass = taskIds === null ? [] : taskIds;
  return SpiderService.exportTasksViewScraperApiV1SpiderTasksExportGet(
    taskIdsToPass,
    url,
    campaignName,
    getCurrentOrg()
  );
};

//TODO: temporary solution until OpenAPI is fixed by provider
export const downloadScrapingTask = async (
  taskId: string,
  fileType: FileTypes = FileTypes.JSON
): Promise<Blob> => {
  const response = await fetch(
    `${process.env.REACT_APP_BASE_API_URL}/scraper/api/v1/spider/tasks/${taskId}/result/download/?file_type=${fileType}`,
    {
      method: 'GET',
      headers: {
        Accept:
          fileType === FileTypes.JSON ? 'application/json' : 'application/zip',
        'x-ai-agent-organization-context': getCurrentOrg(),
      },
      redirect: 'follow',
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Download failed: ${response.status} - ${errorText}`);
  }

  return response.blob();
};

export const deleteScrapingTasks = async (taskIds: string[]): Promise<void> => {
  const response = await fetch(
    `${process.env.REACT_APP_BASE_API_URL}/scraper/api/v1/spider/tasks/remove/`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'x-ai-agent-organization-context': getCurrentOrg(),
      },
      body: JSON.stringify({ ids: taskIds }),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Delete failed: ${response.status} - ${errorText}`);
  }
};

export const getCampaigns = async (): Promise<CampaignsListResponse> => {
  return SpiderService.campaignsListViewScraperApiV1SpiderCampaignsGet(
    getCurrentOrg()
  );
};

export const refreshDemoTasks = async (
  taskIds?: string[] | null,
  campaignName?: string | null
): Promise<void> => {
  return SpiderService.refreshDemoViewScraperApiV1SpiderTasksRefreshDemoPatch(
    taskIds || null,
    campaignName || null,
    getCurrentOrg()
  );
};

export const uploadScreenshot = async (
  taskId: string,
  file: Blob,
  screenshotType: ScreenshotTypes
): Promise<void> => {
  const formData: Body_upload_screenshot_view_scraper_api_v1_spider_tasks__task_id__screenshot__patch =
    {
      screenshot_type: screenshotType,
      file,
    };

  return SpiderService.uploadScreenshotViewScraperApiV1SpiderTasksTaskIdScreenshotPatch(
    taskId,
    formData,
    getCurrentOrg()
  );
};
